import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, CheckCircleOutline as CheckIcon, DeleteOutline as DeleteIcon, Inventory2Outlined as StockIcon,
  Logout as SortieIcon, Undo as UndoIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../../contexts/PermissionContext';
import { articlesAPI, comptoirMatieresAPI } from '../../../services/api';
import { ComptoirMatiereEntreprise } from '../../../types/comptoir';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { Panel, StateChip, num } from '../ui';

const MINI = 'Suivi de production';
const TONE: Record<string, string> = { a_sortir: '#1565C0', sortie: '#2E7D32', a_acheter: '#D32F2F', achetee: '#2E7D32' };

/** Matière de l'entreprise on a service (prototype V8 « Stock »): from the ERP stock (reserved until
 *  « Sortir du stock », which makes a bon de sortie) or to buy. The client's matière is elsewhere. */
const MatiereEntreprisePanel: React.FC<{ serviceId: number; closed: boolean; onError: (m: string) => void }> = ({ serviceId, closed, onError }) => {
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const can = (op: 'add' | 'edit' | 'delete') => hasPermission('Comptoir', op, MINI) && !closed;
  const key = ['comptoir', 'matieres-entreprise', serviceId];
  const { data: rows } = useQuery<ComptoirMatiereEntreprise[]>({ queryKey: key, queryFn: () => comptoirMatieresAPI.list(serviceId), staleTime: 0 });
  const [busy, setBusy] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [retour, setRetour] = useState<ComptoirMatiereEntreprise | null>(null);
  const done = (r: ComptoirMatiereEntreprise[]) => { qc.setQueryData(key, r); qc.invalidateQueries({ queryKey: ['comptoir', 'matieres-stock'] }); };
  const act = async (id: number, fn: () => Promise<ComptoirMatiereEntreprise[]>) => {
    setBusy(id);
    try { done(await fn()); } catch (e: any) { onError(extractErrorMessage(e)); } finally { setBusy(null); }
  };
  const list = rows || [];
  const pend = list.filter((m) => !m.etat.ok).length;
  return (
    <Panel title="Matière de l'entreprise" icon={<StockIcon />} count={list.length ? `${list.length}${pend ? ` · ${pend} à préparer` : ''}` : undefined}
      action={can('add') ? <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Ajouter</Button> : undefined}>
      {!rows ? <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        : !list.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Aucune matière de l'entreprise. Un article du stock est réservé ici, puis déduit à la « Sortie du stock ».
          </Typography>
        ) : list.map((m) => (
          <Stack key={m.id} direction="row" alignItems="center" spacing={1.5}
            sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {m.designation}{m.article_reference ? <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> · {m.article_reference}</Box> : null}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {num(m.quantite)} {m.unite || ''}
                {m.stock && !m.sortie_le ? ` · en stock ${num(m.stock.en_stock)} · réservé ailleurs ${num(m.stock.reserve_ailleurs)}` : ''}
                {m.bon_sortie ? ` · bon ${m.bon_sortie}` : ''}{m.observations ? ` · ${m.observations}` : ''}
              </Typography>
              {m.stock && !m.sortie_le && num(m.stock.disponible) < num(m.quantite) && (
                <Typography variant="caption" color="error" component="div">
                  Manque {num(m.quantite) - Math.max(0, num(m.stock.disponible))} {m.unite || ''} · à acheter
                </Typography>
              )}
            </Box>
            <StateChip small label={m.etat.label} t={{ bg: 'transparent', fg: TONE[m.etat.k], outlined: true }} />
            {m.etat.k === 'a_sortir' && can('add') && (
              <Button size="small" variant="outlined" disabled={busy === m.id} startIcon={busy === m.id ? <CircularProgress size={14} /> : <SortieIcon />}
                onClick={() => act(m.id, () => comptoirMatieresAPI.sortie(m.id))}>Sortir du stock</Button>
            )}
            {m.etat.k === 'a_acheter' && can('edit') && (
              <Button size="small" variant="outlined" disabled={busy === m.id} startIcon={<CheckIcon />}
                onClick={() => act(m.id, () => comptoirMatieresAPI.achetee(m.id))}>Achetée</Button>
            )}
            {m.etat.k === 'sortie' && can('edit') && (
              <Tooltip title="Annuler la sortie de stock"><IconButton size="small" color="warning" onClick={() => setRetour(m)}><UndoIcon fontSize="small" /></IconButton></Tooltip>
            )}
            {!m.sortie_le && can('delete') && (
              <Tooltip title="Retirer"><IconButton size="small" onClick={() => act(m.id, () => comptoirMatieresAPI.remove(m.id))}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            )}
          </Stack>
        ))}
      <AddDialog open={addOpen} onClose={() => setAddOpen(false)}
        onSave={async (d) => { done(await comptoirMatieresAPI.add(serviceId, d)); setAddOpen(false); }} />
      <RetourDialog m={retour} onClose={() => setRetour(null)}
        onSave={async (motif) => { done(await comptoirMatieresAPI.annulerSortie(retour!.id, motif)); setRetour(null); }} />
    </Panel>
  );
};

const AddDialog: React.FC<{ open: boolean; onClose: () => void; onSave: (d: { article_id?: number | null; designation?: string | null; quantite: number; unite?: string | null; observations?: string | null }) => Promise<void> }> = ({ open, onClose, onSave }) => {
  const [src, setSrc] = useState<'stock' | 'achat'>('stock');
  const [article, setArticle] = useState<any | null>(null);
  const [designation, setDesignation] = useState('');
  const [qte, setQte] = useState('');
  const [unite, setUnite] = useState('');
  const [obs, setObs] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const { data: articles } = useQuery({ queryKey: ['comptoir-articles-options'], queryFn: () => articlesAPI.getAllForDropdown(), enabled: open, staleTime: 10 * 60_000 });
  React.useEffect(() => { if (open) { setSrc('stock'); setArticle(null); setDesignation(''); setQte(''); setUnite(''); setObs(''); setErr(''); } }, [open]);
  const save = async () => {
    const q = Number(String(qte).replace(',', '.'));
    if (src === 'stock' && !article) { setErr("Choisissez l'article du stock."); return; }
    if (src === 'achat' && !designation.trim()) { setErr('Indiquez la matière à acheter.'); return; }
    if (!(q > 0)) { setErr('Indiquez la quantité.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave(src === 'stock' ? { article_id: article.id, quantite: q, observations: obs.trim() || null }
        : { designation: designation.trim(), quantite: q, unite: unite.trim() || null, observations: obs.trim() || null });
    } catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Matière de l'entreprise</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <ToggleButtonGroup exclusive size="small" value={src} onChange={(_, v) => v && setSrc(v)} sx={{ mb: 2 }}>
          <ToggleButton value="stock">Du stock</ToggleButton>
          <ToggleButton value="achat">Hors stock · à acheter</ToggleButton>
        </ToggleButtonGroup>
        {src === 'stock' ? (
          <Autocomplete options={articles || []} value={article} onChange={(_, v) => setArticle(v)} loading={!articles}
            getOptionLabel={(a: any) => `${a.reference} · ${a.designation}`} isOptionEqualToValue={(a: any, b: any) => a.id === b.id}
            renderInput={(p) => <TextField {...p} label="Article du stock" required placeholder="Référence ou désignation" />} />
        ) : (
          <Stack direction="row" spacing={1.5}>
            <TextField label="Matière à acheter" required fullWidth value={designation} onChange={(e) => setDesignation(e.target.value)} placeholder="ex : Tube carré 40×40×2" />
            <TextField label="Unité" value={unite} onChange={(e) => setUnite(e.target.value)} sx={{ width: 130 }} placeholder="barre" />
          </Stack>
        )}
        <TextField label="Quantité" required value={qte} onChange={(e) => setQte(e.target.value)} sx={{ mt: 2, width: 160 }} inputProps={{ inputMode: 'decimal' }} />
        <TextField label="Observations" fullWidth value={obs} onChange={(e) => setObs(e.target.value)} sx={{ mt: 2 }} />
        <Alert severity="info" sx={{ mt: 2 }}>
          {src === 'stock'
            ? 'La quantité est réservée pour ce service, puis déduite du stock à la « Sortie du stock » (bon de sortie).'
            : "Elle apparaît dans les besoins d'achat jusqu'à « Achetée »."}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Annuler</Button>
        <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}>Ajouter</Button>
      </DialogActions>
    </Dialog>
  );
};

const RetourDialog: React.FC<{ m: ComptoirMatiereEntreprise | null; onClose: () => void; onSave: (motif: string) => Promise<void> }> = ({ m, onClose, onSave }) => {
  const [motif, setMotif] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { setMotif(''); setErr(''); }, [m]);
  if (!m) return null;
  const save = async () => {
    if (!motif.trim()) { setErr("Indiquez le motif : il est gardé dans l'historique."); return; }
    setSaving(true);
    try { await onSave(motif.trim()); } catch (e: any) { setErr(extractErrorMessage(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Annuler la sortie de stock</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Typography variant="body2" sx={{ mb: 2 }}>
          {num(m.quantite)} {m.unite || ''} de « {m.designation} » reviennent dans le stock ; le bon {m.bon_sortie} passe à la corbeille.
          La matière repasse « À sortir du stock ». Rien n'est effacé de l'historique du stock.
        </Typography>
        <TextField label="Motif" required fullWidth value={motif} onChange={(e) => setMotif(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Fermer</Button>
        <Button variant="contained" color="warning" onClick={save} disabled={saving}>Annuler la sortie</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatiereEntreprisePanel;
